import { createBrowserRouter } from 'react-router'
import { GuestOnly, RequireAuth } from '@/features/auth/require-auth'
import { RouteError } from './route-error'

export const router = createBrowserRouter([
  {
    ErrorBoundary: () => <RouteError />,
    children: [
      {
        path: '/login',
        lazy: async () => {
          const { LoginPage } = await import('@/features/auth/login-page')
          return { Component: () => <GuestOnly><LoginPage /></GuestOnly> }
        },
      },
      {
        path: '/pos',
        lazy: async () => {
          const { PosPage } = await import('@/features/pos/pos-page')
          return { Component: () => <RequireAuth><PosPage /></RequireAuth> }
        },
      },
      {
        path: '/count/:id',
        lazy: async () => {
          const { CountingPage } = await import('@/features/inventory/counting/counting-page')
          return { Component: () => <RequireAuth><CountingPage /></RequireAuth> }
        },
      },
      {
        path: '/system',
        lazy: async () => ({ Component: (await import('@/features/system/status-page')).StatusPage }),
      },
      {
        path: '/',
        lazy: async () => {
          const { AppShell } = await import('./shell/app-shell')
          return { Component: () => <RequireAuth><AppShell /></RequireAuth> }
        },
        children: [
          {
            // Page errors stay inside the shell, so the sidebar is still there to move on.
            ErrorBoundary: () => <RouteError inShell />,
            children: [
              { index: true, lazy: async () => ({ Component: (await import('@/features/home/home-page')).HomePage }) },
              { path: 'users', lazy: async () => ({ Component: (await import('@/features/users/users-page')).UsersPage }) },
              { path: 'users/:id', lazy: async () => ({ Component: (await import('@/features/users/user-detail-page')).UserDetailPage }) },
              { path: 'roles', lazy: async () => ({ Component: (await import('@/features/roles/roles-page')).RolesPage }) },
              { path: 'products', lazy: async () => ({ Component: (await import('@/features/catalog/products-page')).ProductsPage }) },
              { path: 'products/labels', lazy: async () => ({ Component: (await import('@/features/catalog/labels/labels-page')).LabelsPage }) },
              { path: 'products/:id', lazy: async () => ({ Component: (await import('@/features/catalog/product-page')).ProductPage }) },
              { path: 'taxonomy', lazy: async () => ({ Component: (await import('@/features/catalog/taxonomy-page')).TaxonomyPage }) },
              { path: 'inventory/movements', lazy: async () => ({ Component: (await import('@/features/inventory/movements-page')).MovementsPage }) },
              { path: 'inventory/expiring', lazy: async () => ({ Component: (await import('@/features/inventory/expiring/expiring-page')).ExpiringPage }) },
              { path: 'inventory/adjust', lazy: async () => ({ Component: (await import('@/features/inventory/adjust/adjust-page')).AdjustPage }) },
              { path: 'inventory/counts', lazy: async () => ({ Component: (await import('@/features/inventory/counts/counts-page')).CountsPage }) },
              { path: 'inventory/counts/:id', lazy: async () => ({ Component: (await import('@/features/inventory/counts/count-page')).CountPage }) },
              { path: 'suppliers', lazy: async () => { const m = await import('@/features/parties/parties-page'); return { Component: () => <m.PartiesPage type="supplier" /> } } },
              { path: 'suppliers/:id', lazy: async () => { const m = await import('@/features/parties/party-page'); return { Component: () => <m.PartyPage type="supplier" /> } } },
              { path: 'customers', lazy: async () => ({ Component: (await import('@/features/customers/customers-page')).CustomersPage }) },
              { path: 'customers/:id', lazy: async () => ({ Component: (await import('@/features/customers/customer-page')).CustomerPage }) },
              { path: 'salespeople', lazy: async () => ({ Component: (await import('@/features/salespeople/salespeople-page')).SalespeoplePage }) },
              { path: 'promotions', lazy: async () => ({ Component: (await import('@/features/promotions/promotions-page')).PromotionsPage }) },
              { path: 'promotions/:id', lazy: async () => ({ Component: (await import('@/features/promotions/promotion-page')).PromotionPage }) },
              { path: 'reports', lazy: async () => ({ Component: (await import('@/features/reports/reports-page')).ReportsPage }) },
              { path: 'reports/sales', lazy: async () => ({ Component: (await import('@/features/reports/overview-page')).OverviewPage }) },
              { path: 'reports/stock.movement', lazy: async () => ({ Component: (await import('@/features/reports/stock-movement/movement-page')).MovementPage }) },
              { path: 'reports/:key', lazy: async () => ({ Component: (await import('@/features/reports/report-page')).ReportPage }) },
              { path: 'sales', lazy: async () => ({ Component: (await import('@/features/sales/sales-page')).SalesPage }) },
              { path: 'purchases', lazy: async () => ({ Component: (await import('@/features/purchasing/purchases-page')).PurchasesPage }) },
              { path: 'purchases/:id', lazy: async () => ({ Component: (await import('@/features/purchasing/purchase-page')).PurchasePage }) },
              { path: 'cash', lazy: async () => ({ Component: (await import('@/features/cash/accounts/accounts-page')).AccountsPage }) },
              { path: 'cash/expenses', lazy: async () => ({ Component: (await import('@/features/cash/expenses/expenses-page')).ExpensesPage }) },
              { path: 'cash/payments', lazy: async () => ({ Component: (await import('@/features/cash/payments-page')).PaymentsPage }) },
              { path: 'cash/:id', lazy: async () => ({ Component: (await import('@/features/cash/account-page')).AccountPage }) },
              { path: 'settings', lazy: async () => ({ Component: (await import('@/features/settings/settings-page')).SettingsPage }) },
              { path: 'activity', lazy: async () => ({ Component: (await import('@/features/activity/activity-page')).ActivityPage }) },
              { path: 'notes', lazy: async () => ({ Component: (await import('@/features/staff-notes/notes-page')).NotesPage }) },
            ],
          },
        ],
      },
    ],
  },
])
